/**
 * Authentication Service for Thai Security Directory
 * Handles all authentication-related API calls
 */

import { apiClient } from './api';
import {
  LoginCredentials,
  RegisterData,
  AuthResponse,
  User,
  PasswordChangeData,
  PasswordResetData,
  RefreshTokenResponse,
} from '../types/auth';

class AuthService {
  private readonly baseURL = '/api/auth';

  /**
   * Login user with email and password
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await apiClient.post(`${this.baseURL}/login`, credentials);
    return response.data;
  }

  /**
   * Register new user account
   */
  async register(data: RegisterData): Promise<{ message: string; user: User }> {
    // Transform camelCase to snake_case for backend API
    const apiPayload = {
      email: data.email,
      password: data.password,
      confirmPassword: data.confirmPassword,
      firstName: data.firstName,
      lastName: data.lastName,
      phone: data.phone,
      lineId: data.lineId,
      user_category: data.userCategory, // Transform: userCategory → user_category
      role: data.role,
    };

    // Debug log to check what data is being sent
    console.log('Registration data (original):', JSON.stringify(data, null, 2));
    console.log('Registration data (transformed for API):', JSON.stringify(apiPayload, null, 2));

    const response = await apiClient.post(`${this.baseURL}/register`, apiPayload);
    return response.data;
  }

  /**
   * Logout current user
   */
  async logout(): Promise<void> {
    try {
      await apiClient.post(`${this.baseURL}/logout`);
    } catch (error) {
      // Logout should always succeed locally even if API fails
      console.warn('Logout API call failed:', error);
    }
  }

  /**
   * Get current user information
   */
  async getCurrentUser(): Promise<User> {
    const response = await apiClient.get(`${this.baseURL}/me`);
    return response.data;
  }

  /**
   * Update user profile
   */
  async updateProfile(data: Partial<User>): Promise<User> {
    const response = await apiClient.put(`${this.baseURL}/profile`, data);
    return response.data.user;
  }

  /**
   * Change user password
   */
  async changePassword(data: PasswordChangeData): Promise<void> {
    await apiClient.put(`${this.baseURL}/password`, data);
  }

  /**
   * Request password reset
   */
  async requestPasswordReset(email: string): Promise<{ message: string }> {
    const response = await apiClient.post(`${this.baseURL}/password-reset`, { email });
    return response.data;
  }

  /**
   * Confirm password reset with token
   */
  async confirmPasswordReset(data: PasswordResetData): Promise<{ message: string }> {
    const response = await apiClient.post(`${this.baseURL}/password-reset/confirm`, data);
    return response.data;
  }

  /**
   * Refresh access token
   */
  async refreshToken(refreshToken: string): Promise<RefreshTokenResponse> {
    const response = await apiClient.post(`${this.baseURL}/refresh`, {
      refresh_token: refreshToken
    });
    return response.data;
  }

  /**
   * Email verification removed - users can login immediately after registration
   */
  // async verifyEmail(token: string): Promise<{ message: string }> {
  //   const response = await apiClient.post(`${this.baseURL}/verify-email`, { token });
  //   return response.data;
  // }

  /**
   * Email verification removed - users can login immediately after registration
   */
  // async resendEmailVerification(): Promise<{ message: string }> {
  //   const response = await apiClient.post(`${this.baseURL}/resend-verification`);
  //   return response.data;
  // }

  /**
   * Check if user has specific permission (Updated for 3-Role System)
   */
  hasPermission(user: User | null, permission: string): boolean {
    if (!user) return false;

    const permissionMap: { [key: string]: string[] } = {
      // Admin-only permissions
      'create_company': ['ADMIN', 'SUPER_ADMIN'],
      'edit_any_company': ['ADMIN', 'SUPER_ADMIN'],
      'delete_company': ['ADMIN', 'SUPER_ADMIN'],
      'verify_company': ['ADMIN', 'SUPER_ADMIN'],
      'manage_users': ['ADMIN', 'SUPER_ADMIN'],
      'ban_users': ['ADMIN', 'SUPER_ADMIN'],
      'view_admin_panel': ['ADMIN', 'SUPER_ADMIN'],
      'manage_system': ['SUPER_ADMIN'],
      'view_analytics': ['ADMIN', 'SUPER_ADMIN'],
      'approve_requests': ['ADMIN', 'SUPER_ADMIN'],

      // Member permissions (includes legacy roles)
      'claim_company': ['MEMBER', 'USER', 'COMPANY_OWNER', 'ADMIN', 'SUPER_ADMIN'],
      'create_change_request': ['MEMBER', 'USER', 'COMPANY_OWNER', 'ADMIN', 'SUPER_ADMIN'],
      'view_advanced_search': ['MEMBER', 'USER', 'COMPANY_OWNER', 'ADMIN', 'SUPER_ADMIN'],
      'view_contact_info_owned': ['MEMBER', 'USER', 'COMPANY_OWNER', 'ADMIN', 'SUPER_ADMIN'],
    };

    const allowedRoles = permissionMap[permission] || [];
    return allowedRoles.includes(user.role);
  }

  /**
   * Check if user can manage companies (Updated for 3-Role System)
   */
  canManageCompanies(user: User | null): boolean {
    if (!user) return false;
    // Legacy support: All members can claim companies
    return ['MEMBER', 'USER', 'COMPANY_OWNER', 'ADMIN', 'SUPER_ADMIN'].includes(user.role);
  }

  /**
   * Check if user is admin (Updated for 3-Role System)
   */
  isAdmin(user: User | null): boolean {
    if (!user) return false;
    return ['ADMIN', 'SUPER_ADMIN'].includes(user.role);
  }

  /**
   * Check if user is member (New for 3-Role System)
   */
  isMember(user: User | null): boolean {
    if (!user) return false;
    // All authenticated users are members (including legacy roles)
    return ['MEMBER', 'USER', 'COMPANY_OWNER'].includes(user.role) || this.isAdmin(user);
  }

  /**
   * Check if user is guest (New for 3-Role System)
   */
  isGuest(user: User | null): boolean {
    // Guest = not authenticated
    return !user;
  }

  /**
   * Check if user is super admin (Legacy support)
   */
  isSuperAdmin(user: User | null): boolean {
    if (!user) return false;
    return user.role === 'SUPER_ADMIN';
  }

  /**
   * Get user role display name in Thai (Updated for 3-Role System)
   */
  getRoleDisplayName(role: string): string {
    const roleNames: { [key: string]: string } = {
      // New simplified roles
      'ADMIN': 'ผู้ดูแลระบบ',
      'MEMBER': 'สมาชิก',

      // Legacy roles (for backward compatibility)
      'SUPER_ADMIN': 'ผู้ดูแลระบบสูงสุด',
      'COMPANY_OWNER': 'เจ้าของบริษัท',
      'USER': 'ผู้ใช้ทั่วไป',
    };

    return roleNames[role] || role;
  }

  /**
   * Validate password strength (simplified requirements)
   */
  validatePasswordStrength(password: string): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (password.length < 6) {
      errors.push('รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร');
    }

    if (password.length > 50) {
      errors.push('รหัสผ่านต้องไม่เกิน 50 ตัวอักษร');
    }

    // Check that password contains only alphanumeric characters (a-z, A-Z, 0-9)
    if (!/^[a-zA-Z0-9\s]*$/.test(password.replace(/\s/g, ''))) {
      errors.push('รหัสผ่านต้องประกอบด้วยตัวอักษรและตัวเลขเท่านั้น (a-z, A-Z, 0-9)');
    }

    // Check for at least one letter (a-z or A-Z)
    if (!/[a-zA-Z]/.test(password)) {
      errors.push('รหัสผ่านต้องมีตัวอักษรอย่างน้อย 1 ตัว');
    }

    // Check for at least one number (0-9)
    if (!/[0-9]/.test(password)) {
      errors.push('รหัสผ่านต้องมีตัวเลขอย่างน้อย 1 ตัว');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Format user full name
   */
  getFullName(user: User): string {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    } else if (user.firstName) {
      return user.firstName;
    } else if (user.lastName) {
      return user.lastName;
    } else {
      return user.email.split('@')[0];
    }
  }

  /**
   * Get user avatar URL or initials
   */
  getAvatarUrl(user: User): string | null {
    return user.avatarUrl || null;
  }

  /**
   * Get user initials for avatar placeholder
   */
  getUserInitials(user: User): string {
    const fullName = this.getFullName(user);
    const names = fullName.split(' ');

    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    } else if (names.length === 1) {
      return names[0][0].toUpperCase();
    } else {
      return user.email[0].toUpperCase();
    }
  }

  /**
   * Email verification removed - all users can login immediately
   */
  // isEmailVerified(user: User): boolean {
  //   return true;  // All users verified now
  // }

  /**
   * Get profile completion percentage
   */
  getProfileCompletion(user: User): number {
    let completed = 0;
    const fields = [
      'email',
      'firstName',
      'lastName',
      'phone',
      'lineId',
      // 'isEmailVerified'  // Email verification removed
    ];

    fields.forEach(field => {
      if (user[field as keyof User]) {
        completed++;
      }
    });

    return Math.round((completed / fields.length) * 100);
  }

  /**
   * Format Thai phone number
   */
  formatPhoneNumber(phone: string | null | undefined): string {
    // Handle null, undefined, or empty phone numbers
    if (!phone) return '';

    // Ensure it's a string
    if (typeof phone !== 'string') return '';

    // Remove all non-digits
    const digits = phone.replace(/\D/g, '');

    // Format as xxx-xxx-xxxx
    if (digits.length === 10 && digits.startsWith('0')) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    }

    return phone;
  }

  /**
   * Validate Thai phone number
   */
  isValidThaiPhone(phone: string): boolean {
    if (!phone) return false;

    const digits = phone.replace(/\D/g, '');
    return digits.length === 10 && digits.startsWith('0');
  }

  /**
   * Get user status display
   */
  getUserStatusDisplay(user: User): { text: string; color: string } {
    // Email verification removed - check only isActive
    // if (!user.isEmailVerified) {
    //   return { text: 'รอการยืนยันอีเมล', color: 'yellow' };
    // }

    if (!user.isActive) {
      return { text: 'ไม่ใช้งาน', color: 'red' };
    }

    return { text: 'ใช้งานได้', color: 'green' };
  }
}

// Export singleton instance
export const authService = new AuthService();